(() => {
  let videoSequence = 0;
  const configureVideoCards = root => {
    root.querySelectorAll('[data-resource-card][data-format="VIDEO"]').forEach(card => {
      card.removeAttribute('target');
      card.removeAttribute('download');
      card.setAttribute('aria-haspopup', 'dialog');
    });
  };
  const videoSource = value => {
    let url;
    try { url = new URL(value, location.origin); } catch { return null; }
    if (url.protocol !== 'https:') return null;
    const youtubeHosts = ['youtube.com', 'www.youtube.com', 'm.youtube.com', 'youtube-nocookie.com', 'www.youtube-nocookie.com'];
    let id;
    if (youtubeHosts.includes(url.hostname)) {
      id = url.searchParams.get('v') || url.pathname.match(/^\/(?:shorts|embed|live|v)\/([a-zA-Z0-9_-]{11})(?:\/|$)/)?.[1];
    } else if (['youtu.be', 'www.youtu.be'].includes(url.hostname)) {
      id = url.pathname.split('/').filter(Boolean)[0];
    }
    if (id && /^[a-zA-Z0-9_-]{11}$/.test(id)) {
      const embed = new URL('https://www.youtube-nocookie.com/embed/' + id);
      embed.searchParams.set('autoplay', '1');
      embed.searchParams.set('rel', '0');
      embed.searchParams.set('playsinline', '1');
      const time = url.searchParams.get('start') || url.searchParams.get('t') || '';
      const units = time.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s)?$/);
      const start = /^\d+$/.test(time) ? Number(time) : units ? Number(units[1] || 0) * 3600 + Number(units[2] || 0) * 60 + Number(units[3] || 0) : 0;
      if (start > 0 && Number.isSafeInteger(start)) embed.searchParams.set('start', String(start));
      return {type: 'iframe', src: embed.href};
    }
    if (['vimeo.com', 'www.vimeo.com', 'player.vimeo.com'].includes(url.hostname)) {
      const videoId = url.pathname.match(/(?:^|\/)(\d+)(?:\/|$)/)?.[1];
      if (videoId) {
        const embed = new URL('https://player.vimeo.com/video/' + videoId);
        embed.searchParams.set('autoplay', '1');
        const hash = url.searchParams.get('h');
        if (hash && /^[a-zA-Z0-9]+$/.test(hash)) embed.searchParams.set('h', hash);
        return {type: 'iframe', src: embed.href};
      }
    }
    if (/\.(mp4|m4v|webm|ogv|ogg)$/i.test(url.pathname)) return {type: 'video', src: url.href};
    return null;
  };
  const initializeVideos = root => {
    if (root.dataset.resourceVideoInitialized) return;
    root.dataset.resourceVideoInitialized = 'true';
    configureVideoCards(root);
    root.addEventListener('click', event => {
      const card = event.target.closest('[data-resource-card][data-format="VIDEO"]');
      if (!card || !root.contains(card) || event.defaultPrevented || event.button > 0) return;
      event.preventDefault();
      document.querySelector('.ks-resource-video-dialog[open]')?.close();
      const source = videoSource(card.dataset.videoUrl || card.href);
      const dialog = document.createElement('dialog');
      dialog.className = 'ks-resource-video-dialog';
      dialog.style.setProperty('--ks-resource-font', getComputedStyle(root).getPropertyValue('--ks-resource-font'));
      const title = document.createElement('h2');
      title.id = 'ks-resource-video-title-' + (++videoSequence);
      title.textContent = card.querySelector('h3')?.textContent || card.textContent.trim();
      dialog.setAttribute('aria-labelledby', title.id);
      const header = document.createElement('div');
      header.className = 'ks-resource-video-dialog__header';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'ks-resource-video-dialog__close';
      close.setAttribute('aria-label', root.dataset.videoClose);
      close.textContent = '×';
      close.autofocus = true;
      close.addEventListener('click', () => dialog.close());
      header.append(title, close);
      const stage = document.createElement('div');
      stage.className = 'ks-resource-video-dialog__stage';
      const error = document.createElement('p');
      error.className = 'ks-resource-video-dialog__error';
      error.textContent = root.dataset.videoError;
      error.hidden = Boolean(source);
      stage.append(error);
      let player;
      if (source?.type === 'iframe') {
        player = document.createElement('iframe');
        player.src = source.src;
        player.title = title.textContent;
        player.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        player.allowFullscreen = true;
        player.referrerPolicy = 'strict-origin-when-cross-origin';
      } else if (source?.type === 'video') {
        player = document.createElement('video');
        player.src = source.src;
        player.controls = true;
        player.autoplay = true;
        player.playsInline = true;
        player.setAttribute('aria-label', title.textContent);
        player.addEventListener('error', () => { player.hidden = true; error.hidden = false; });
      }
      if (player) stage.append(player);
      dialog.append(header, stage);
      let backdropStart = false;
      const outside = event => {
        const bounds = dialog.getBoundingClientRect();
        return event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom;
      };
      dialog.addEventListener('pointerdown', event => { backdropStart = event.target === dialog && outside(event); });
      dialog.addEventListener('click', event => {
        if (backdropStart && event.target === dialog && outside(event)) dialog.close();
        backdropStart = false;
      });
      dialog.addEventListener('close', () => {
        if (player?.tagName === 'VIDEO') {
          player.pause();
          player.removeAttribute('src');
          player.load();
        }
        dialog.remove();
        if (!document.querySelector('.ks-resource-video-dialog[open]')) {
          document.documentElement.classList.remove('ks-resource-video-open');
          if (card.isConnected) card.focus({preventScroll: true});
        }
      }, {once: true});
      document.body.append(dialog);
      document.documentElement.classList.add('ks-resource-video-open');
      dialog.showModal();
    });
  };
  const normalized = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const previewLinks = root => {
    const themeId = new URL(location.href).searchParams.get('preview_theme_id');
    if (!themeId) return;
    root.querySelectorAll('a[href^="/pages/"]').forEach(anchor => {
      const url = new URL(anchor.getAttribute('href'), location.origin);
      url.searchParams.set('preview_theme_id', themeId);
      anchor.href = url.toString();
    });
  };
  class ResourceLibrary {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-resource-form]');
      this.grid = root.querySelector('[data-resource-grid]');
      this.status = root.querySelector('[data-resource-status]');
      this.empty = root.querySelector('[data-resource-empty]');
      this.pagination = root.querySelector('[data-resource-pagination]');
      this.pageSize = 12;
      this.page = 1;
      this.media = root.dataset.category === 'media';
      this.names = ['q', 'product', 'brand', this.media ? 'topic' : 'type'];
      this.readCards();
      this.updateOptions();
      this.readURL();
      this.form.hidden = false;
      this.form.addEventListener('submit', event => { event.preventDefault(); this.page = 1; this.render(true); });
      this.form.addEventListener('change', () => { this.page = 1; this.render(true); });
      this.form.elements.q.addEventListener('input', () => {
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => { this.page = 1; this.render(true); }, 180);
      });
      this.root.querySelector('[data-resource-reset]').addEventListener('click', () => {
        this.form.reset(); this.page = 1; this.render(true);
      });
      this.pagination.addEventListener('click', event => {
        const button = event.target.closest('[data-resource-page]');
        if (!button || button.disabled) return;
        this.page = Number(button.dataset.resourcePage);
        this.render(true);
        this.root.querySelector('.ks-resource-library__heading').scrollIntoView({behavior: 'auto', block: 'start'});
        this.pagination.querySelector('[aria-current="page"]')?.focus({preventScroll: true});
      });
      window.addEventListener('popstate', () => { this.readURL(); this.render(); });
      this.render();
      this.loadAdditional();
    }
    readCards() {
      this.cards = [...this.grid.querySelectorAll('[data-resource-card]')].sort((a, b) =>
        a.querySelector('h3').textContent.localeCompare(b.querySelector('h3').textContent, 'en', {numeric: true}));
      this.cards.forEach(card => this.grid.append(card));
      configureVideoCards(this.root);
    }
    updateOptions() {
      const fields = {product: 'productCategory', brand: 'brand', type: 'format', topic: 'topic'};
      for (const name of this.names.filter(name => name !== 'q')) {
        const select = this.form.elements[name];
        const selected = select.value;
        const placeholder = select.options[0].text;
        const values = [...new Set(this.cards.map(card => card.dataset[fields[name]]).filter(Boolean))].sort();
        select.replaceChildren(new Option(placeholder, ''));
        values.forEach(value => select.add(new Option(value, value)));
        if (selected && !values.includes(selected)) select.add(new Option(selected, selected));
        select.value = selected;
      }
    }
    readURL() {
      const params = new URL(location.href).searchParams;
      for (const name of this.names) {
        const value = params.get(name) || '';
        const field = this.form.elements[name];
        if (field.tagName === 'SELECT' && value && ![...field.options].some(option => option.value === value)) field.add(new Option(value, value));
        field.value = value;
      }
      this.page = Math.max(1, Number.parseInt(params.get('rpage') || '1', 10) || 1);
    }
    updateURL() {
      const url = new URL(location.href);
      for (const name of this.names) {
        const value = this.form.elements[name].value.trim();
        if (value) url.searchParams.set(name, value); else url.searchParams.delete(name);
      }
      if (this.page > 1) url.searchParams.set('rpage', this.page); else url.searchParams.delete('rpage');
      history.replaceState(null, '', url);
    }
    render(writeURL = false) {
      const values = Object.fromEntries(this.names.map(name => [name, this.form.elements[name].value]));
      const terms = normalized(values.q).trim().split(/\s+/).filter(Boolean);
      const matches = this.cards.filter(card =>
        terms.every(term => normalized(card.dataset.search).includes(term)) &&
        (!values.product || card.dataset.productCategory === values.product) &&
        (!values.brand || card.dataset.brand === values.brand) &&
        (!values.type || card.dataset.format === values.type) &&
        (!values.topic || card.dataset.topic === values.topic));
      const pages = Math.max(1, Math.ceil(matches.length / this.pageSize));
      this.page = Math.min(this.page, pages);
      this.cards.forEach(card => { card.hidden = true; });
      const start = (this.page - 1) * this.pageSize;
      matches.slice(start, start + this.pageSize).forEach(card => { card.hidden = false; });
      this.empty.hidden = matches.length > 0;
      const noCategory = this.cards.length === 0;
      this.empty.querySelectorAll('[data-empty-filtered]').forEach(node => { node.hidden = noCategory; });
      this.empty.querySelectorAll('[data-empty-category]').forEach(node => { node.hidden = !noCategory; });
      this.root.querySelector('[data-resource-reset]').hidden = noCategory;
      this.status.textContent = this.status.dataset.pattern.replace('[from]', matches.length ? start + 1 : 0).replace('[to]', Math.min(start + this.pageSize, matches.length)).replace('[total]', matches.length);
      this.pagination.replaceChildren();
      this.pagination.hidden = pages < 2;
      const addButton = (text, page, label, disabled = false, current = false) => {
        const button = document.createElement('button');
        button.type = 'button'; button.textContent = text; button.dataset.resourcePage = page;
        button.setAttribute('aria-label', label); button.disabled = disabled;
        if (current) button.setAttribute('aria-current', 'page');
        this.pagination.append(button);
      };
      if (pages > 1) {
        addButton('←', this.page - 1, this.pagination.dataset.previous, this.page === 1);
        for (let page = 1; page <= pages; page++) {
          if (pages > 7 && page > 1 && page < pages && Math.abs(page - this.page) > 1) {
            if (page === 2 || page === pages - 1) { const gap = document.createElement('span'); gap.textContent = '…'; this.pagination.append(gap); }
            continue;
          }
          addButton(String(page), page, this.pagination.dataset.pageLabel.replace('[page]', page), false, page === this.page);
        }
        addButton('→', this.page + 1, this.pagination.dataset.next, this.page === pages);
      }
      if (writeURL) this.updateURL();
    }
    async loadAdditional() {
      let next = this.grid.querySelector('[data-resource-next]');
      const visited = new Set();
      while (next && !visited.has(next.href)) {
        const url = new URL(next.href, location.origin);
        if (url.origin !== location.origin) return;
        visited.add(next.href);
        url.searchParams.set('section_id', this.root.dataset.sectionId);
        const themeId = new URL(location.href).searchParams.get('preview_theme_id');
        if (themeId) url.searchParams.set('preview_theme_id', themeId);
        try {
          const response = await fetch(url, {credentials: 'same-origin'});
          if (!response.ok) return;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const grid = html.querySelector('[data-resource-grid]');
          if (!grid) return;
          const known = new Set(this.cards.map(card => card.dataset.handle));
          grid.querySelectorAll('[data-resource-card]').forEach(card => {
            if (!known.has(card.dataset.handle)) this.grid.append(card);
          });
          next.remove();
          next = grid.querySelector('[data-resource-next]');
          if (next) this.grid.append(next);
          this.readCards(); this.updateOptions(); this.render(); previewLinks(this.root);
        } catch { return; }
      }
    }
  }
  const initialize = scope => {
    scope.querySelectorAll('[data-ks-resources]').forEach(root => {
      if (root.dataset.resourcesInitialized) return;
      root.dataset.resourcesInitialized = 'true';
      previewLinks(root);
      initializeVideos(root);
      if (root.hasAttribute('data-resource-library')) new ResourceLibrary(root);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(document)); else initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();

(() => {
  const categories = new Set(['planning', 'installation', 'ordering', 'certificates', 'media']);
  const paths = new Set(['/pages/knox-system-planning-specification', '/pages/knox-system-installation-setup', '/pages/knox-system-ordering-configuration', '/pages/knox-system-certificates-warranty', '/pages/knox-system-catalogues-media']);
  const initializeFilters = scope => {
    if (!paths.has(location.pathname.replace(/\/$/, ''))) return;
    const roots = [...scope.querySelectorAll('.ks-resources[data-resource-library]')];
    if (scope.matches?.('.ks-resources[data-resource-library]')) roots.unshift(scope);
    roots.forEach(root => {
      if (!categories.has(root.dataset.category) || root.dataset.filterStyleInitialized) return;
      const form = root.querySelector('[data-resource-form]');
      if (!form) return;
      root.dataset.filterStyleInitialized = 'true';
      const dropdowns = [];
      const closeAll = except => dropdowns.forEach(dropdown => { if (dropdown !== except) dropdown.close(); });
      form.querySelectorAll('.ks-resource-select>select').forEach((select, index) => {
        const oldLabel = select.parentElement;
        const label = oldLabel.querySelector('span');
        const field = document.createElement('div');
        field.className = oldLabel.className + ' ks-resource-select--custom';
        oldLabel.replaceWith(field);
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ks-resource-select__trigger';
        button.setAttribute('role', 'combobox');
        button.setAttribute('aria-haspopup', 'listbox');
        button.setAttribute('aria-expanded', 'false');
        const id = 'ks-filter-' + root.dataset.sectionId + '-' + select.name + '-' + index;
        label.className = 'ks-resource-select__label';
        label.id = id + '-label';
        const value = document.createElement('span');
        value.className = 'ks-resource-select__value';
        value.id = id + '-value';
        button.setAttribute('aria-labelledby', label.id + ' ' + value.id);
        const chevron = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        chevron.setAttribute('class', 'ks-resource-select__chevron');
        chevron.setAttribute('viewBox', '0 0 12 8');
        chevron.setAttribute('aria-hidden', 'true');
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('d', 'M1 1.5 6 6.5 11 1.5');
        path.setAttribute('fill', 'none');
        path.setAttribute('stroke', 'currentColor');
        path.setAttribute('stroke-width', '1.5');
        chevron.append(path);
        button.append(label, value, chevron);
        const list = document.createElement('div');
        list.className = 'ks-resource-select__list';
        list.id = id + '-list';
        list.hidden = true;
        list.setAttribute('role', 'listbox');
        list.setAttribute('aria-labelledby', label.id);
        button.setAttribute('aria-controls', list.id);
        select.tabIndex = -1;
        select.setAttribute('aria-hidden', 'true');
        field.append(button, select, list);
        let active = -1;
        let typeBuffer = '';
        let typeTimer;
        const options = () => [...select.options];
        const clearActive = () => {
          list.querySelectorAll('[data-keyboard-active]').forEach(option => option.removeAttribute('data-keyboard-active'));
          button.removeAttribute('aria-activedescendant');
        };
        const activate = position => {
          const rows = [...list.children];
          if (!rows.length) return;
          active = Math.max(0, Math.min(position, rows.length - 1));
          clearActive();
          rows[active].setAttribute('data-keyboard-active', '');
          button.setAttribute('aria-activedescendant', rows[active].id);
          rows[active].scrollIntoView({block: 'nearest'});
        };
        const close = () => {
          list.hidden = true;
          button.setAttribute('aria-expanded', 'false');
          clearActive();
          typeBuffer = '';
        };
        const sync = () => {
          value.textContent = select.selectedOptions[0]?.textContent || '';
          button.disabled = select.disabled;
          [...list.children].forEach((row, position) => row.setAttribute('aria-selected', String(position === select.selectedIndex)));
        };
        const choose = position => {
          const option = options()[position];
          if (!option || option.disabled) return;
          select.selectedIndex = position;
          sync();
          close();
          select.dispatchEvent(new Event('change', {bubbles: true}));
          button.focus({preventScroll: true});
        };
        const rebuild = () => {
          list.replaceChildren();
          options().forEach((option, position) => {
            const row = document.createElement('button');
            row.type = 'button';
            row.className = 'ks-resource-select__option';
            row.id = id + '-option-' + position;
            row.tabIndex = -1;
            row.setAttribute('role', 'option');
            row.disabled = option.disabled;
            const text = document.createElement('span');
            text.textContent = option.textContent;
            row.append(text);
            row.addEventListener('click', () => choose(position));
            row.addEventListener('pointerenter', () => { active = position; clearActive(); });
            list.append(row);
          });
          sync();
        };
        const open = keyboard => {
          closeAll(dropdown);
          sync();
          list.hidden = false;
          button.setAttribute('aria-expanded', 'true');
          active = Math.max(0, select.selectedIndex);
          if (keyboard) activate(active);
        };
        const dropdown = {field, button, close, sync};
        dropdowns.push(dropdown);
        button.addEventListener('click', () => list.hidden ? open(false) : close());
        button.addEventListener('keydown', event => {
          if (event.key === 'Escape' || event.key === 'Tab') { close(); return; }
          if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
            event.preventDefault();
            const wasClosed = list.hidden;
            if (wasClosed) open(true);
            if (event.key === 'Home') activate(0);
            else if (event.key === 'End') activate(list.children.length - 1);
            else if (!wasClosed) {
              const step = event.key === 'ArrowDown' ? 1 : -1;
              let next = active + step;
              while (next >= 0 && next < select.options.length && select.options[next].disabled) next += step;
              if (next >= 0 && next < select.options.length) activate(next);
            }
          } else if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (list.hidden) open(true); else choose(active);
          } else if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            event.preventDefault();
            if (list.hidden) open(true);
            clearTimeout(typeTimer);
            typeBuffer += event.key.toLocaleLowerCase();
            const match = options().findIndex(option => !option.disabled && option.textContent.toLocaleLowerCase().startsWith(typeBuffer));
            if (match >= 0) activate(match);
            typeTimer = setTimeout(() => { typeBuffer = ''; }, 600);
          }
        });
        rebuild();
        const observer = new MutationObserver(rebuild);
        observer.observe(select, {childList: true, subtree: true, characterData: true, attributes: true});
        dropdown.observer = observer;
      });
      const syncAll = () => dropdowns.forEach(dropdown => dropdown.sync());
      const outside = event => { if (!dropdowns.some(dropdown => dropdown.field.contains(event.target))) closeAll(); };
      const focusOutside = event => dropdowns.forEach(dropdown => { if (!dropdown.field.contains(event.target)) dropdown.close(); });
      form.addEventListener('change', syncAll);
      form.addEventListener('reset', () => queueMicrotask(() => { closeAll(); syncAll(); }));
      form.addEventListener('submit', () => closeAll());
      document.addEventListener('pointerdown', outside);
      document.addEventListener('focusin', focusOutside);
      const popstate = () => queueMicrotask(() => { closeAll(); syncAll(); });
      window.addEventListener('popstate', popstate);
      const unload = event => {
        if (!event.target.contains(root)) return;
        dropdowns.forEach(dropdown => dropdown.observer.disconnect());
        document.removeEventListener('pointerdown', outside);
        document.removeEventListener('focusin', focusOutside);
        window.removeEventListener('popstate', popstate);
        document.removeEventListener('shopify:section:unload', unload);
      };
      document.addEventListener('shopify:section:unload', unload);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initializeFilters(document)); else initializeFilters(document);
  document.addEventListener('shopify:section:load', event => initializeFilters(event.target));
})();
